import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { workflowKeys } from "../use-workflows";
import { isPending, REGRESSION_POLL_MS, type RegressionCaseBody } from "./regression-model";

/** Under the workflow's own key, so every save, publish and run (which invalidate `workflowKeys.all`) refresh them. */
export const regressionKeys = {
  cases: (ws: string, id: string) => [...workflowKeys.detail(ws, id), "regression-cases"] as const,
  latest: (ws: string, id: string) => [...workflowKeys.detail(ws, id), "regression-latest"] as const,
};

export function useRegressionCases(workflowId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: regressionKeys.cases(ws, workflowId),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/regression-cases", { params: { path: { workflow_id: workflowId } } })),
    retry: false,
  });
}

/** The last run (`null`: never run); polls while it is queued or running. */
export function useLatestRun(workflowId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: regressionKeys.latest(ws, workflowId),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/regression-runs/latest", { params: { path: { workflow_id: workflowId } } })),
    refetchInterval: (query) => (isPending(query.state.data) ? REGRESSION_POLL_MS : false),
    retry: false,
  });
}

export function useRegressionMutations(workflowId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const path = { params: { path: { workflow_id: workflowId } } };
  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: regressionKeys.cases(ws, workflowId) }),
    client.invalidateQueries({ queryKey: regressionKeys.latest(ws, workflowId) }),
  ]);
  const create = useMutation({ mutationFn: (body: RegressionCaseBody) => unwrap(api.POST("/v1/workflows/{workflow_id}/regression-cases", { ...path, body })), onSuccess: refresh });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: RegressionCaseBody }) => unwrap(api.PATCH("/v1/workflows/{workflow_id}/regression-cases/{case_id}", { params: { path: { workflow_id: workflowId, case_id: id } }, body })),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/workflows/{workflow_id}/regression-cases/{case_id}", { params: { path: { workflow_id: workflowId, case_id: id } } })),
    onSuccess: refresh,
  });
  const run = useMutation({ mutationFn: () => unwrap(api.POST("/v1/workflows/{workflow_id}/regression-runs", path)), onSuccess: refresh });
  /** Publish after a passing run, or with the override (`workflows.publish_override`, audited with its reason). */
  const publish = useMutation({
    mutationFn: (override: { reason: string } | null) => unwrap(api.POST("/v1/workflows/{workflow_id}/publish", { ...path, body: { override_regression: override !== null, reason: override?.reason ?? null } })),
    onSuccess: (detail) => {
      client.setQueryData(workflowKeys.detail(ws, workflowId), detail);
      return client.invalidateQueries({ queryKey: workflowKeys.all(ws) });
    },
  });
  return { create, update, remove, run, publish };
}
